import { lazy, Suspense, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../auth/AuthProvider';
import { RemoteErrorBoundary } from '../components/RemoteErrorBoundary';
import { useT } from '../i18n';

/** The federated underwriting workbench remote, loaded on demand at /workbench. */
const RemoteWorkbenchApp = lazy(() => import('workbench/App'));

export function WorkbenchRoute() {
  const { user, loading, login, logout, roles, permissions } = useAuth();
  const t = useT();

  useEffect(() => {
    if (!loading && !user) {
      login();
    }
  }, [loading, user, login]);

  if (loading) {
    return (
      <Box className="flex justify-center py-12">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box className="flex justify-center py-12">
        <CircularProgress />
      </Box>
    );
  }

  // Platform Admin — modules, workflow & document configuration. No
  // underwriting authority: the console is their whole surface.
  if (roles.includes('ADMIN')) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <RemoteErrorBoundary fallbackMessage={t('Feature unavailable — is workbench running?')}>
      <Suspense
        fallback={
          <Box className="flex justify-center py-12">
            <CircularProgress />
          </Box>
        }
      >
        <RemoteWorkbenchApp
          userName={
            (user.profile.name as string | undefined) ??
            (user.profile.preferred_username as string | undefined) ??
            user.profile.email ??
            undefined
          }
          userEmail={user.profile.email}
          permissions={permissions}
          userSub={user.profile.sub}
          onSignOut={logout}
        />
      </Suspense>
    </RemoteErrorBoundary>
  );
}
